// pages/AboutPage.js

import React from "react";
import {
  FaServer,
  FaRocket,
  FaMicroscope,
  FaCoins,
  FaPlayCircle,
  FaHammer,
  FaBookOpen,
  FaDownload,
  FaDatabase,
  FaHdd,
  FaBrain,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";
import "./AboutPage.css";

const CHARGE_ROWS = [
  {
    icon: <FaPlayCircle />,
    name: "Pipeline run (compute)",
    when: "Each time a pipeline run finishes",
    basis:
      "The actual vCPU-hours and memory (GB-hours) consumed by your pipeline's steps on AWS. Cached / reused steps are free, and a run that is resumed or relaunched is billed only once.",
  },
  {
    icon: <FaHammer />,
    name: "First-time workflow build",
    when: "Once, on a workflow's first successful run",
    basis:
      "A flat one-time build fee (currently 50 credits) covering AI-assisted container generation, validation and setup. Later runs of the same workflow are never re-charged this fee.",
  },
  {
    icon: <FaBookOpen />,
    name: "Interactive notebooks",
    when: "While a Jupyter notebook session is active",
    basis:
      "The vCPU and memory of your notebook, metered per hour of active session time. Billing stops automatically when the session is closed or idles out.",
  },
  {
    icon: <FaDownload />,
    name: "Result downloads",
    when: "When you download pipeline result files",
    basis:
      "The volume of data transferred out of the cloud (per GB of egress). Browsing and previewing results in-app is not charged.",
  },
  {
    icon: <FaDatabase />,
    name: "Public data imports",
    when: "When an SRA / public dataset import completes",
    basis:
      "The size of the data retrieved — approximately 1 credit per GB, with a 1-credit minimum per file.",
  },
  {
    icon: <FaHdd />,
    name: "Cloud storage (S3)",
    when: "Monthly",
    basis:
      "The average amount of data kept in your workspace over the calendar month (per GB-month). Removing data you no longer need lowers this charge.",
  },
  {
    icon: <FaBrain />,
    name: "AI interpretation reports",
    when: "When an AI result report completes",
    basis:
      "The size and complexity of the generated report (AI tokens used). You are only billed for reports that finish successfully.",
  },
];

const BILLING_HIGHLIGHTS = [
  {
    title: "Prepaid credits",
    text:
      "All usage draws from your organization's prepaid credit balance, topped up securely through Stripe. You never receive a surprise invoice — once credits run low, you simply add more.",
  },
  {
    title: "Pay only for what you use",
    text:
      "Compute is metered on the real CPU and memory your pipeline consumes on AWS — not on reserved capacity or wall-clock time you didn't use.",
  },
  {
    title: "No double-charging on retries",
    text:
      "Cached pipeline steps cost nothing, and resumed or relaunched runs of the same job are billed a single time — automatic safeguards prevent duplicate charges.",
  },
  {
    title: "One-time build, then run freely",
    text:
      "The first successful run of a new workflow includes a flat build fee for AI container generation and validation. Every run after that is charged only for the compute it uses.",
  },
  {
    title: "A transparent, itemized ledger",
    text:
      "Every charge is recorded in your billing history with its type, amount and the exact run or download it relates to, so costs are always traceable.",
  },
];

const AboutPage = () => {
  return (
    <div className="about-page">
      <header className="about-hero">
        <h1 className="about-hero__title">About CloudGenomics</h1>
        <p className="about-hero__tagline">
          AI-powered workflow designer and executor for scalable genomics pipelines
        </p>
      </header>

      <section className="about-card">
        <h2 className="about-card__title">
          <span className="about-card__icon">
            <FaServer />
          </span>
          AI-powered pipelines, AWS-powered execution
        </h2>
        <p className="about-card__body">
          CloudGenomics is a powerful AI-driven platform that enables seamless execution and
          recovery of scientific workflows, particularly in genomics and bioinformatics. What sets
          it apart is its ability to autonomously diagnose and correct errors — whether in container
          configuration or pipeline logic — without requiring any manual infrastructure handling.
          Users can initiate, monitor, and requeue complex jobs with ease, thanks to built-in
          self-healing mechanisms and intelligent fault tolerance. This makes it especially valuable
          for research labs and pharma organizations where operational resilience, reliability, and
          reproducibility are paramount. Its intuitive interface and automated backend remove the
          steep learning curve traditionally associated with data-intensive pipelines. Whether a job
          fails due to misconfiguration or runtime error, the system leverages AI to generate
          corrected scripts, re-launch jobs, and maintain structured logs for full transparency.
        </p>
      </section>

      <section className="about-card">
        <h2 className="about-card__title">
          <span className="about-card__icon">
            <FaRocket />
          </span>
          Just ask a question, leave everything else to us
        </h2>
        <p className="about-card__body">
          At its heart is an AI-powered, interview-style interface that guides users through a
          conversation, asking just the right questions to gather all necessary inputs. Instead of
          manually configuring pipeline parameters or scripting workflows, users simply respond to
          the AI's prompts — and behind the scenes, their responses are transformed into precise,
          optimized pipeline configurations. Once the workflow is assembled, the platform
          automatically launches it into a resilient cloud execution environment, monitors logs in
          real time, detects failure patterns, and invokes AI-based corrections when something goes
          wrong. Corrected files are uploaded, jobs are requeued, and users can watch the recovery
          unfold in a clear, auditable interface — a system built for researchers who want to focus
          on discovery, not DevOps.
        </p>
      </section>

      <section className="about-card about-charges">
        <h2 className="about-card__title">
          <span className="about-card__icon">
            <FaCoins />
          </span>
          How charges work
        </h2>
        <p className="about-card__body">
          CloudGenomics uses a simple, usage-based credit model. You keep a prepaid balance of
          credits, and the platform meters the cloud resources each activity actually consumes —
          compute, storage and data transfer — so your bill reflects real usage with no fixed
          seats or hidden fees.
        </p>

        <ul className="about-highlights">
          {BILLING_HIGHLIGHTS.map((item) => (
            <li className="about-highlight" key={item.title}>
              <FaCheckCircle className="about-highlight__check" />
              <div>
                <span className="about-highlight__title">{item.title}</span>
                <span className="about-highlight__text">{item.text}</span>
              </div>
            </li>
          ))}
        </ul>

        <h3 className="about-charges__subtitle">What you are charged for</h3>
        <div className="about-table-wrap">
          <table className="about-table">
            <thead>
              <tr>
                <th>Transaction</th>
                <th>When it applies</th>
                <th>What it's based on</th>
              </tr>
            </thead>
            <tbody>
              {CHARGE_ROWS.map((row) => (
                <tr key={row.name}>
                  <td className="about-table__name">
                    <span className="about-table__icon">{row.icon}</span>
                    {row.name}
                  </td>
                  <td className="about-table__when">{row.when}</td>
                  <td className="about-table__basis">{row.basis}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="about-note">
          <FaInfoCircle className="about-note__icon" />
          <span>
            Compute actually consumed by a failed run may still be billed, because real AWS
            resources were used. Exact rates depend on your organization's plan and are itemized on
            every charge in your billing history.
          </span>
        </p>
      </section>

      <footer className="about-footer">
        <p>
          <FaMicroscope /> Built with precision and purpose.
        </p>
      </footer>
    </div>
  );
};

export default AboutPage;
